import type { FC } from 'react';
import {
  AccountBalanceOutline,
  ChevronRight,
} from '@eqi-investimentos/icons/web';
import { Flex, Link, Text } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { appsStoreLinks } from '@/config/appsStoreLinks';
import { MODAL_CONNECTORS_ID } from '@/components/modules/InvestModal';
import { OpenAccountDrawer } from '@/components/modules/OpenAccountDrawer';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';
import { IOS_USER_AGENT_REGEX } from '@/utils/regex';

export const AppsAndBrokersSection: FC<AppsAndBrokersSectionProps> = ({
  balance,
}) => {
  const t = useT();

  const openConnectorsDrawer = () =>
    window.dispatchEvent(new Event(`open-chakra-modal-${MODAL_CONNECTORS_ID}`));

  return (
    <Flex flexDir="column" gap="m" px="2xl" w="full">
      <IfRender condition={balance === 0}>
        <Flex
          justify="space-between"
          cursor="pointer"
          pb="m"
          borderBottom="1px solid"
          borderColor="border.secondary"
          onClick={openConnectorsDrawer}
        >
          <Flex gap="xs">
            <AccountBalanceOutline w={24} h={24} color="content.brand" />
            <Text size="xl" color="content.secondary">
              {t('screens.invest.appsBrokers.conectBrokers')}
            </Text>
          </Flex>
          <ChevronRight w={24} h={24} color="content.brand" />
        </Flex>
      </IfRender>
      <OpenAccountDrawer
        screen="invest"
        customButton={
          <Flex
            flexDir="row"
            justify="space-between"
            cursor="pointer"
            pb="m"
            borderBottom="1px solid"
            borderColor="border.secondary"
          >
            <Flex gap="xs">
              <Thumb
                src="/assets/eqi-logo.svg"
                width={24}
                height={24}
                fit="cover"
                alt="Logotipo 'EQI' em branco sobre fundo verde-escuro."
              />
              <Text size="xl" color="content.secondary">
                {t('screens.invest.appsBrokers.eqiBroker')}
              </Text>
            </Flex>

            <ChevronRight w={24} h={24} color="content.brand" />
          </Flex>
        }
      />
      <Link
        href={getAppStoreLinkByOS()}
        target="_blank"
        rel="noopener noreferrer"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py="s"
        w="full"
        borderBottom="1px solid"
        borderColor="border.secondary"
      >
        <Flex alignItems="center" w="full" gap="xs">
          <Thumb
            src="/assets/eqi_trader.svg"
            width={24}
            height={24}
            fit="cover"
            alt="Logotipo quadrado com o fundo verde claro e as letras EQI em verde escuro"
          />
          <Text size="xl" color="content.secondary">
            {t('screens.invest.appsBrokers.eqiTrader')}
          </Text>
        </Flex>
        <ChevronRight w={24} h={24} color="content.brand" />
      </Link>
    </Flex>
  );
};

const getAppStoreLinkByOS = () => {
  const isIOS = IOS_USER_AGENT_REGEX.test(navigator.userAgent);
  if (isIOS) {
    return appsStoreLinks.eqi.trader.ios;
  }
  return appsStoreLinks.eqi.trader.android;
};

type AppsAndBrokersSectionProps = {
  balance: number;
};
